import { i18n } from '@/plugins'
import { nextTick } from 'vue'

export const random = (len = 8): string => {
  const src = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let str = ''
  for (let i = 0; i < len; i++) {
    str += src[Math.floor(Math.random() * src.length)]
  }
  return str
}

// ref: https://github.com/MartinKolarik/dedent-js/blob/master/src/index.ts
export const dedent = (str: string): string => {
  const matches = []
  const strs = [str]
  strs[strs.length - 1] = strs[strs.length - 1].replace(/\r?\n([\t ]*)$/, '')
  for (let i = 0; i < strs.length; i++) {
    let match
    if ((match = strs[i].match(/\n[\t ]+/g))) {
      matches.push(...match)
    }
  }
  if (matches.length) {
    const size = Math.min(...matches.map((value) => value.length - 1))
    const pattern = new RegExp(`\n[\t ]{${size}}`, 'g')
    for (let i = 0; i < strs.length; i++) {
      strs[i] = strs[i].replace(pattern, '\n')
    }
  }
  strs[0] = strs[0].replace(/^\r?\n/, '')
  return strs[0]
}

export const hanToZen = (v: string): string => {
  return v.replace(/[!-~]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) + 0xfee0)
  )
}

export const parameterize = (value: string, separator = '_'): string => {
  const v = value
  const s = separator
  return v
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove non-word characters except hyphen and space
    .replace(/[\s]+/g, s) // Replace spaces with separator
    .replace(new RegExp(`([a-z\\d])([A-Z])`, 'g'), `$1${s}$2`) // Convert camel case to separator-separated
    .toLowerCase() // Convert to lowercase
}

export const getCodeNameStr = (item: Record<string, any>) => {
  return item ? `${item.code}: ${item.name}` : null
}

export const getBuyerCodeStr = (buyer: Record<string, any>) => {
  return buyer.branch_code
    ? `${buyer.code}-${buyer.branch_code}`
    : `${buyer.code}`
}

export const getBuyerCodeName = (buyer: Record<string, any>) => {
  return buyer ? `${getBuyerCodeStr(buyer)}: ${buyer.name}` : null
}

export const concatBuyerCodeName = (
  buyer: Record<string, any> | null,
  uridomeQuerySelector: string | null = null
) => {
  if (!buyer || !buyer?.code) return null
  const isClearance = buyer?.is_clearance || false
  const buyerStr = buyer
    ? `${getBuyerCodeStr(buyer)}: ${
        isClearance ? i18n.t('common.uridome') : ''
      } ${buyer.name}`
    : null
  if (uridomeQuerySelector !== null)
    nextTick(() =>
      highlightTextWithClass(
        uridomeQuerySelector,
        i18n.t('common.uridome') as string
      )
    )
  return buyerStr
}

export const highlightTextWithClass = (
  querySelector: string,
  textToColor: string,
  className = 'red--text',
  type: 'all' | 'single' = 'all'
): boolean => {
  if (!textToColor.trim()) return false

  const escapedText = textToColor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedText})`, 'gi')

  try {
    const elements =
      type === 'single'
        ? [document.querySelector(querySelector)].filter(Boolean)
        : Array.from(document.querySelectorAll(querySelector))

    if (elements.length === 0) return false

    elements.forEach((el) => {
      if (el) highlightTextNodesWithClass(el, regex, className)
    })

    return true
  } catch (error) {
    console.error('Error highlighting text with class:', error)
    return false
  }
}

const highlightTextNodesWithClass = (
  element: Element,
  regex: RegExp,
  className: string
): void => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.classList.contains(className)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT
  })

  const textNodes: Text[] = []
  let node

  while ((node = walker.nextNode())) {
    textNodes.push(node as Text)
  }

  textNodes.forEach((textNode) => {
    const text = textNode.textContent
    if (!text || !regex.test(text)) return

    regex.lastIndex = 0
    const highlightedHTML = text.replace(
      regex,
      `<span class="${className}">$1</span>`
    )

    if (highlightedHTML !== text) {
      const wrapper = document.createElement('span')
      wrapper.innerHTML = highlightedHTML
      textNode.parentNode?.replaceChild(wrapper, textNode)
    }
  })
}

export const sortShipper = (
  shipper1obj: any,
  shipper2obj: any,
  isDesc: boolean
) => {
  // shipperオブジェクトの構造が異なる場合の処理
  const shipper1 = {
    prefecture_code: shipper1obj.code ? shipper1obj.code.split('-')[0] : '',
    organization_type_code: shipper1obj.code
      ? shipper1obj.code.split('-')[1]
      : '',
    agri_group_code: shipper1obj.code ? shipper1obj.code.split('-')[2] : '',
    personal_code: shipper1obj.code ? shipper1obj.code.split('-')[3] : ''
  }
  const shipper2 = {
    prefecture_code: shipper2obj.code ? shipper2obj.code.split('-')[0] : '',
    organization_type_code: shipper2obj.code
      ? shipper2obj.code.split('-')[1]
      : '',
    agri_group_code: shipper2obj.code ? shipper2obj.code.split('-')[2] : '',
    personal_code: shipper2obj.code ? shipper2obj.code.split('-')[3] : ''
  }

  // Helper function to pad strings with leading zeros
  function lpad(str: any, length: any) {
    str = String(str)
    while (str.length < length) {
      str = '0' + str
    }
    return str
  }

  // Helper function to handle null/undefined values, similar to SQL COALESCE
  function coalesce(value: any, defaultValue: any) {
    return value === null || value === undefined ? defaultValue : value
  }

  // Determine max lengths.  Important for correct string comparison.
  const maxPrefectureCodeLength = Math.max(
    String(coalesce(shipper1.prefecture_code, '')).length,
    String(coalesce(shipper2.prefecture_code, '')).length
  )
  const maxOrganizationTypeCodeLength = Math.max(
    String(coalesce(shipper1.organization_type_code, '')).length,
    String(coalesce(shipper2.organization_type_code, '')).length
  )
  const maxAgriGroupCodeLength = Math.max(
    String(coalesce(shipper1.agri_group_code, '0')).length,
    String(coalesce(shipper2.agri_group_code, '0')).length
  )
  const maxPersonalCodeLength = Math.max(
    String(coalesce(shipper1.personal_code, '0')).length,
    String(coalesce(shipper2.personal_code, '0')).length
  )

  // Pad the relevant fields for comparison
  const paddedPrefectureCode1 = lpad(
    coalesce(shipper1.prefecture_code, ''),
    maxPrefectureCodeLength
  )
  const paddedPrefectureCode2 = lpad(
    coalesce(shipper2.prefecture_code, ''),
    maxPrefectureCodeLength
  )
  const paddedOrganizationTypeCode1 = lpad(
    coalesce(shipper1.organization_type_code, ''),
    maxOrganizationTypeCodeLength
  )
  const paddedOrganizationTypeCode2 = lpad(
    coalesce(shipper2.organization_type_code, ''),
    maxOrganizationTypeCodeLength
  )
  const paddedAgriGroupCode1 = lpad(
    coalesce(shipper1.agri_group_code, '0'),
    maxAgriGroupCodeLength
  )
  const paddedAgriGroupCode2 = lpad(
    coalesce(shipper2.agri_group_code, '0'),
    maxAgriGroupCodeLength
  )
  const paddedPersonalCode1 = lpad(
    coalesce(shipper1.personal_code, '0'),
    maxPersonalCodeLength
  )
  const paddedPersonalCode2 = lpad(
    coalesce(shipper2.personal_code, '0'),
    maxPersonalCodeLength
  )

  // Perform the comparison
  let comparisonResult = 0
  if (paddedPrefectureCode1 !== paddedPrefectureCode2) {
    comparisonResult = paddedPrefectureCode1.localeCompare(
      paddedPrefectureCode2
    )
  } else if (paddedOrganizationTypeCode1 !== paddedOrganizationTypeCode2) {
    comparisonResult = paddedOrganizationTypeCode1.localeCompare(
      paddedOrganizationTypeCode2
    )
  } else if (paddedAgriGroupCode1 !== paddedAgriGroupCode2) {
    comparisonResult = paddedAgriGroupCode1.localeCompare(paddedAgriGroupCode2)
  } else {
    comparisonResult = paddedPersonalCode1.localeCompare(paddedPersonalCode2)
  }

  // Adjust for ascending/descending order
  return isDesc ? -comparisonResult : comparisonResult
}

interface NormalizeOptions {
  trim?: boolean
  emptyToNull?: boolean
}

/**
 * Perform string normalization processing
 * @param str Target string
 * @param options  (Default: trim=true, emptyToNull=true)
 */
export const normalizeString = (
  str: string | null | undefined,
  options: NormalizeOptions = {}
): string | null => {
  const { trim = true, emptyToNull = true } = options

  if (typeof str !== 'string') {
    return emptyToNull ? null : ''
  }

  let result = str

  if (trim) {
    result = result.trim()
  }

  if (emptyToNull && result === '') {
    return null
  }

  return result
}

/**
 * Perform code normalization processing each time text is entered.
 * @param str Target string
 */
export const normalizeCode = (str: string | null | undefined): string => {
  if (!str) return ''
  return str.replace(/\s+/g, '')
}

